import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import SearchBar from '../components/SearchBar';
import PackageCard from '../components/PackageCard';
import WhyChooseUs from '../components/WhyChooseUs';
import IndiaTourismVision from '../components/IndiaTourismVision';
import SEO from '../components/SEO';
import { destinationAPI, packageAPI, reviewAPI } from '../services/api';
import { newsletterAPI } from '../services/newsletterAPI';
import { toUploadUrl } from '../config/api';

const Home = () => {
    const [featuredPackages, setFeaturedPackages] = useState([]);
    const [trendingPackages, setTrendingPackages] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Newsletter state
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [subscribeError, setSubscribeError] = useState('');

    // Helper function to get image URL
    const getImageUrl = (imagePath) => {
        return toUploadUrl(imagePath, {
            fallback: 'https://placehold.co/400x300/1a1a1a/ffffff?text=No+Image'
        });
    };

    const fetchHomeData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [packagesResult, trendingResult, destinationsResult] = await Promise.allSettled([
                packageAPI.getAllPackages(),
                packageAPI.getTrendingPackages(),
                destinationAPI.getFeaturedDestinations(5)
            ]);

            const allPackages = getResponseArray(packagesResult);
            const trendingPackagesData = getResponseArray(trendingResult);
            const destinationData = getResponseArray(destinationsResult);

            if (!allPackages.length && !trendingPackagesData.length && !destinationData.length) {
                throw new Error('No homepage data returned from API');
            }

            const featured = allPackages.slice(0, 4);
            const trending = trendingPackagesData.length ? trendingPackagesData : allPackages.filter((pkg) => pkg.trending).slice(0, 4);
            const packageIds = Array.from(new Set([...featured, ...trending].map((pkg) => pkg.id).filter(Boolean)));
            let reviewSummaryMap = {};

            if (packageIds.length) {
                try {
                    const reviewSummaryRes = await reviewAPI.getSummary('package', packageIds);
                    reviewSummaryMap = (reviewSummaryRes.data?.data || []).reduce((acc, item) => {
                        acc[item.entity_id] = item;
                        return acc;
                    }, {});
                } catch (reviewError) {
                    console.error('Error fetching package review summary:', reviewError);
                }
            }

            const attachReviewSummary = (pkg) => ({
                ...pkg,
                average_rating: reviewSummaryMap[pkg.id]?.average_rating || pkg.average_rating || pkg.rating || 0,
                review_count: reviewSummaryMap[pkg.id]?.review_count || pkg.review_count || pkg.reviews || 0
            });

            setFeaturedPackages(featured.map(attachReviewSummary));
            setTrendingPackages(trending.map(attachReviewSummary));
            setDestinations(destinationData.slice(0, 5));
        } catch (err) {
            setError('Failed to load data. Please try again.');
            console.error('Error fetching home data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHomeData();
    }, [fetchHomeData]);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        setSubscribing(true);
        setSubscribeError('');
        
        try {
            await newsletterAPI.subscribe(email);
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 5000);
        } catch (err) {
            setSubscribeError(err.response?.data?.message || 'Subscription failed. Please try again.');
        } finally {
            setSubscribing(false);
        }
    };

    const offers = [
        {
            title: 'Signup Bonus',
            description: 'Get an instant signup bonus of Rs. 200 on your account after registration.',
            code: 'BONUS200',
            validTill: '31 Mar 2026',
            gradient: 'linear-gradient(135deg, #334f7f 0%, #764ba2 100%)',
            icon: 'fas fa-user-plus'
        },
        {
            title: 'Booking Cashback',
            description: 'Earn cashback up to Rs. 1000 per person on eligible package bookings.',
            code: 'CASHBACK1000',
            validTill: '31 Mar 2026',
            gradient: 'linear-gradient(135deg, #6b8cff 0%, #4a6cf7 100%)',
            icon: 'fas fa-wallet'
        },
        {
            title: 'Free Ride Combo',
            description: 'Enjoy Gandola ride plus Shikara ride absolutely free with selected bookings.',
            code: 'FREEFUN',
            validTill: '31 Mar 2026',
            gradient: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)',
            icon: 'fas fa-ticket-alt'
        }
    ];

    const testimonials = [
        {
            name: 'Rahul Sharma',
            location: 'Delhi',
            comment: 'Amazing experience with RedGoTrip! The Kashmir tour was well organized and the houseboat stay was memorable.',
            rating: 5,
            image: toUploadUrl('rahul.png'),
            date: 'Feb 2026'
        },
        {
            name: 'Priya Singh',
            location: 'Mumbai',
            comment: 'Great service and very responsive team. Our honeymoon trip was perfect. The Gulmarg gondola ride was fantastic!',
            rating: 5,
            image: toUploadUrl('priya.png'),
            date: 'Jan 2026'
        },
        {
            name: 'Amit Kumar',
            location: 'Bangalore',
            comment: 'Everything was well planned. Will book again! Special thanks to the team for making our trip special.',
            rating: 4,
            image: toUploadUrl('amit.png'),
            date: 'Mar 2026'
        }
    ];

    const heroStats = [
        { value: '2500+', label: 'Happy Travelers' },
        { value: '150+', label: 'Curated Packages' },
        { value: '4.8/5', label: 'Average Rating' },
        { value: '24/7', label: 'Travel Support' }
    ];

    const planningHighlights = [
        {
            icon: 'fas fa-route',
            title: 'Curated Routes',
            text: 'Balanced itineraries with stays, sightseeing and transport planned together.'
        },
        {
            icon: 'fas fa-wallet',
            title: 'Value-First Deals',
            text: 'Best-selling packages, early-bird savings and seasonal offers in one place.'
        },
        {
            icon: 'fas fa-headset',
            title: 'Human Support',
            text: 'Real travel guidance before booking, during the trip and after check-in.'
        }
    ];

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading amazing destinations...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <i className="fas fa-exclamation-circle"></i>
                <h3>Oops! Something went wrong</h3>
                <p>{error}</p>
                <button onClick={fetchHomeData} className="retry-btn">
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <>
            <SEO
                title="RedGoTrip | Holiday Packages, Camping, Destinations & Deals"
                description="Book holiday packages, camping stays and scenic destinations with RedGoTrip. Explore deals, cashback offers, destination guides and expert travel support."
                canonicalPath="/"
                keywords="RedGoTrip, holiday packages, India tour packages, camping stays, Kashmir tour, Goa packages, family trips"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'TravelAgency',
                    name: 'RedGoTrip',
                    url: 'https://redgotrips.com',
                    telephone: '+91 9335444047',
                    description: 'Holiday packages, destinations, camping stays and travel support by RedGoTrip.'
                }}
            />
            <HeroSection />
            <SearchBar />

            <section className="home-highlights">
                <div className="home-highlights-grid">
                    {heroStats.map((item, index) => (
                        <div key={index} className="highlight-stat-card">
                            <strong>{item.value}</strong>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="home-planning-strip">
                <div className="planning-strip-header">
                    <h2>Plan Better Holidays With RedGoTrip</h2>
                    <p>Packages, destinations and travel support designed to make booking simpler and trips smoother.</p>
                </div>
                <div className="planning-strip-grid">
                    {planningHighlights.map((item, index) => (
                        <div key={index} className="planning-card">
                            <div className="planning-icon">
                                <i className={item.icon}></i>
                            </div>
                            <h3>{item.title}</h3>
                            <p>{item.text}</p>
                        </div>
                    ))}
                </div>
            </section>
            
            <section className="destinations-section">
                <div className="section-header">
                    <h2>Popular Destinations in India</h2>
                    <p>Explore the most beautiful places in paradise on earth</p>
                    <Link to="/destinations" className="view-all">
                        View All Destinations <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
                <div className="destinations-grid">
                    {destinations.length > 0 ? (
                        destinations.map((dest, index) => (
                            <div key={dest.id || index} className="destination-card">
                                <Link to={`/destinations/${dest.id}`}>
                                    <img 
                                        src={getImageUrl(dest.image)} 
                                        alt={dest.name}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://placehold.co/400x300/1a1a1a/ffffff?text=No+Image';
                                        }}
                                    />
                                    <div className="destination-info">
                                        <h3>{dest.name}</h3>
                                        <p className="destination-tours">{dest.ideal_duration || '2-3 days'}</p>
                                        <p className="destination-price">
                                             <i className="fas fa-star" style={{ color: '#ffc107' }}></i> 
                                            {dest.rating || '4.5'} ({dest.reviews || 0} reviews)
                                        </p>
                                    </div>
                                </Link>
                            </div>
                        ))
                    ) : (
                        <div className="no-destinations">
                            <p>No destinations found</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="packages-section">
                <div className="section-header">
                    <h2>Featured Holidays Tour Packages</h2>
                    <p>Handpicked tours for an unforgettable experience</p>
                    <Link to="/packages" className="view-all">
                        View All Packages <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
                <div className="packages-grid">
                    {featuredPackages.length > 0 ? (
                        featuredPackages.map(pkg => (
                            <PackageCard key={pkg.id} package={pkg} />
                        ))
                    ) : (
                        <p className="no-data">No featured packages available</p>
                    )}
                </div>
            </section>

            <section className="offers-section">
                <div className="section-header">
                    <h2>Special Offers & Deals</h2>
                    <p>Grab the best deals on Holiday's tour packages</p>
                </div>
                <div className="offers-grid">
                    {offers.map((offer, index) => (
                        <div key={index} className="offer-card" style={{ background: offer.gradient }}>
                            <div className="offer-icon">
                                <i className={offer.icon}></i>
                            </div>
                            <h3>{offer.title}</h3>
                            <p>{offer.description}</p>
                            {/* <div className="offer-code">
                                <span>Use Code:</span> <strong>{offer.code}</strong>
                            </div> */}
                            <div className="offer-valid">
                                <i className="far fa-clock"></i> Valid till: {offer.validTill}
                            </div>
                            <Link to="/packages" className="offer-btn">
                                Grab This Offer <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>
                    ))}
                </div>
            </section>

            <section className="packages-section">
                <div className="section-header">
                    <h2>Trending Now</h2>
                    <p>Most popular packages among travelers</p>
                    <Link to="/packages?trending=true" className="view-all">
                        View All <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
                <div className="packages-grid">
                    {trendingPackages.length > 0 ? (
                        trendingPackages.map(pkg => (
                            <PackageCard key={pkg.id} package={pkg} />
                        ))
                    ) : (
                        <p className="no-data">No trending packages available</p>
                    )}
                </div>
            </section>

            <WhyChooseUs />

            <section className="testimonials-section">
                <div className="section-header">
                    <h2>What Our Travelers Say</h2>
                    <p>Real experiences from our happy customers</p>
                    <Link to="/testimonials" className="view-all">
                        Read More Reviews <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <div key={index} className="testimonial-card">
                            <div className="testimonial-header">
                                <img 
                                    src={testimonial.image} 
                                    alt={testimonial.name}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://placehold.co/60x60/1a1a1a/ffffff?text=User';
                                    }}
                                />
                                <div>
                                    <h4>{testimonial.name}</h4>
                                    <p className="testimonial-location">{testimonial.location}</p>
                                    <p className="testimonial-date">{testimonial.date}</p>
                                </div>
                            </div>
                            <div className="testimonial-rating">
                                {[...Array(5)].map((_, i) => (
                                    <i 
                                        key={i} 
                                        className={`fas fa-star ${i < testimonial.rating ? 'filled' : ''}`}
                                        style={{ color: i < testimonial.rating ? '#ffc107' : '#e2e8f0' }}
                                    ></i>
                                ))}
                            </div>
                            <p className="testimonial-comment">"{testimonial.comment}"</p>
                        </div>
                    ))}
                </div>
            </section>

            <IndiaTourismVision />

            <section className="newsletter-section">
                <div className="newsletter-container">
                    <div className="newsletter-content">
                        <h2>Get Travel Inspiration & Deals</h2>
                        <p>Subscribe to our newsletter and get exclusive offers, travel tips, and updates straight to your inbox.</p>
                        
                        {subscribed && (
                            <div className="success-message">
                                <i className="fas fa-check-circle"></i>
                                <p>Thank you for subscribing! Check your email for confirmation.</p>
                            </div>
                        )}
                        
                        {subscribeError && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                <p>{subscribeError}</p>
                            </div>
                        )}
                        
                        <form className="newsletter-form" onSubmit={handleSubscribe}>
                            <div className="form-group">
                                <input 
                                    type="email" 
                                    placeholder="Enter your email address" 
                                    className="newsletter-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={subscribing}
                                />
                                <button 
                                    type="submit" 
                                    className="newsletter-btn"
                                    disabled={subscribing}
                                >
                                    {subscribing ? (
                                        <>Subscribing <i className="fas fa-spinner fa-spin"></i></>
                                    ) : (
                                        <>Subscribe <i className="fas fa-paper-plane"></i></>
                                    )}
                                </button>
                            </div>
                            <div className="form-checkbox">
                                <input type="checkbox" id="privacy" required />
                                <label htmlFor="privacy">
                                    I agree to the <Link to="/privacy">privacy policy</Link> and <Link to="/terms">terms of service</Link>
                                </label>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

function getResponseArray(result) {
    if (result.status !== 'fulfilled') {
        console.error('Homepage API request failed:', result.reason);
        return [];
    }

    const payload = result.value?.data;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.packages)) return payload.packages;
    if (Array.isArray(payload?.destinations)) return payload.destinations;

    return [];
}

export default Home;
